/* Il giornale, in sola lettura.
 *
 * **I filtri vivono nell'indirizzo.** Una ricerca nel giornale e' quasi sempre
 * una domanda che si gira a qualcun altro — «guarda chi ha cambiato questa
 * soglia il 12» — e uno stato interno rende quel collegamento inutile, perche'
 * chi lo apre vede l'elenco intero. Con i filtri nell'indirizzo il collegamento
 * *e'* la domanda, e un ricaricamento non la perde.
 *
 * **Non c'e' niente da premere che scriva.** Nessun tasto modifica o cancella
 * un evento, e non e' una dimenticanza: un evento che si puo' correggere non
 * prova niente, e il giornale esiste per provare. Nemmeno la coda offline lo
 * tocca — cio' che si consulta non si mette in una coda di invio.
 *
 * **I tetti li dichiara il servizio.** Cento per pagina e diecimila per
 * esportazione arrivano nella risposta: una copia nella pagina invecchierebbe, e
 * una richiesta oltre il tetto viene rifiutata invece di essere troncata —
 * un'estrazione troncata somiglia in tutto a una completa.
 */
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ServiceError } from '../../api'
import {
  exportAudit,
  parseAuditFilter,
  readAudit,
  serializeAuditFilter,
  type AuditFilter,
  type AuditPage,
} from '../../admin/audit'
import { moment } from '../../format'
import { useLocale, useT } from '../../i18n/LocaleProvider'
import { t as translate } from '../../i18n/t'

export function AuditLog(): JSX.Element {
  const t = useT()
  const { locale } = useLocale()
  const navigate = useNavigate()
  const location = useLocation()
  // La verita' sta nell'indirizzo: lo stato del componente e' una copia di
  // lavoro delle caselle, non la ricerca in corso.
  const applied = parseAuditFilter(location.search)
  const [draft, setDraft] = useState<AuditFilter>(applied)
  const [page, setPage] = useState<AuditPage | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [sheet, setSheet] = useState<string | null>(null)

  const serialized = serializeAuditFilter(applied)

  useEffect(() => {
    // Le caselle si riallineano quando l'indirizzo cambia da fuori: tasto
    // indietro, collegamento incollato, segnalibro.
    setDraft(parseAuditFilter(serialized))
  }, [serialized])

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    void readAudit(parseAuditFilter(serialized))
      .then((found) => {
        if (!cancelled) setPage(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'audit.readFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [serialized, locale])

  useEffect(() => {
    return () => {
      if (sheet !== null) URL.revokeObjectURL(sheet)
    }
  }, [sheet])

  function apply(next: AuditFilter): void {
    // Una ricerca nuova torna alla prima pagina: restare alla settima con
    // filtri diversi mostrerebbe un elenco vuoto che sembra «non e' successo
    // niente».
    navigate({ pathname: '/admin/audit', search: serializeAuditFilter(next) })
  }

  function onSearch(event: React.FormEvent): void {
    event.preventDefault()
    apply({ ...draft, page: 1 })
  }

  function onPage(next: number): void {
    apply({ ...applied, page: next })
  }

  function onExport(): void {
    setFailure(null)
    void exportAudit(applied)
      .then((blob) => {
        if (sheet !== null) URL.revokeObjectURL(sheet)
        setSheet(URL.createObjectURL(blob))
      })
      .catch((error: unknown) => {
        setFailure(read(error, t('audit.exportFailed')))
      })
  }

  return (
    <main className="amministrazione giornale">
      <h2>{t('title.audit')}</h2>

      <form onSubmit={onSearch} aria-label={t('audit.filters')}>
        <label>
          {t('audit.since')}
          <input
            type="date"
            value={draft.since}
            onChange={(event) => setDraft({ ...draft, since: event.target.value })}
          />
        </label>
        <label>
          {t('audit.until')}
          <input
            type="date"
            value={draft.until}
            onChange={(event) => setDraft({ ...draft, until: event.target.value })}
          />
        </label>
        <label>
          {t('audit.action')}
          <input
            value={draft.action}
            onChange={(event) => setDraft({ ...draft, action: event.target.value })}
            maxLength={64}
            placeholder="settings.version_created"
          />
        </label>
        <label>
          {t('audit.actor')}
          <input
            value={draft.actor}
            onChange={(event) => setDraft({ ...draft, actor: event.target.value })}
            maxLength={36}
            placeholder={t('audit.actorPlaceholder')}
          />
        </label>
        <label>
          {t('audit.subjectType')}
          <input
            value={draft.subject_type}
            onChange={(event) => setDraft({ ...draft, subject_type: event.target.value })}
            maxLength={64}
            placeholder="setting"
          />
        </label>
        <label>
          {t('audit.subject')}
          <input
            value={draft.subject_id}
            onChange={(event) => setDraft({ ...draft, subject_id: event.target.value })}
            maxLength={64}
          />
        </label>
        <label>
          {t('audit.pageSize')}
          <input
            type="number"
            min={1}
            // Il tetto lo dichiara il servizio: finche' non ha risposto si usa
            // quello della prima pagina, e non una costante scritta qui.
            max={page?.max_size ?? undefined}
            value={draft.size}
            onChange={(event) =>
              setDraft({ ...draft, size: Number(event.target.value) || draft.size })
            }
          />
        </label>
        <button type="submit" className="primario">
          {t('common.search')}
        </button>
      </form>

      {failure !== null && <p role="alert">{failure}</p>}

      {page === null ? (
        <p role="status">{t('audit.loading')}</p>
      ) : (
        <>
          <p role="status">
            {page.total === 0
              ? isFiltered(applied)
                ? t('audit.noMatch')
                : t('audit.empty')
              : t('audit.pageSummary', { total: page.total, page: page.page, pages: page.pages })}
          </p>

          <table>
            <thead>
              <tr>
                <th scope="col">{t('audit.colWhen')}</th>
                <th scope="col">{t('audit.colAction')}</th>
                <th scope="col">{t('audit.colWho')}</th>
                <th scope="col">{t('audit.colSubject')}</th>
                <th scope="col">{t('audit.colDetail')}</th>
              </tr>
            </thead>
            <tbody>
              {page.events.map((event) => (
                <tr key={event.id}>
                  <td>{moment(event.at)}</td>
                  <td>
                    <code>{event.action}</code>
                  </td>
                  <td>
                    {event.actor_id === null ? (
                      <span className="tenue">{t('audit.service')}</span>
                    ) : (
                      <code>{event.actor_id}</code>
                    )}
                  </td>
                  <td>
                    {event.subject_type === null ? (
                      <span className="tenue">{t('common.dash')}</span>
                    ) : (
                      <>
                        {event.subject_type} <code>{event.subject_id}</code>
                      </>
                    )}
                  </td>
                  <td>
                    {event.detail === null || event.detail === undefined ? (
                      <span className="tenue">{t('common.dash')}</span>
                    ) : (
                      <code>{JSON.stringify(event.detail)}</code>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label={t('audit.pages')}>
            <button type="button" disabled={page.page <= 1} onClick={() => onPage(page.page - 1)}>
              {t('common.prevPage')}
            </button>
            <button
              type="button"
              disabled={page.page >= page.pages}
              onClick={() => onPage(page.page + 1)}
            >
              {t('common.nextPage')}
            </button>
          </nav>

          <p>
            {sheet === null ? (
              <button type="button" onClick={onExport}>
                {t('audit.prepareExport')}
              </button>
            ) : (
              <a href={sheet} download="audit.csv">
                {t('audit.downloadExport')}
              </a>
            )}
          </p>
          <p className="tenue">
            {t('audit.limitsNote', { maxSize: page.max_size, maxExport: page.max_export })}
          </p>
        </>
      )}
    </main>
  )
}

function isFiltered(filter: AuditFilter): boolean {
  return (
    filter.since !== '' ||
    filter.until !== '' ||
    filter.actor !== '' ||
    filter.action !== '' ||
    filter.subject_type !== '' ||
    filter.subject_id !== ''
  )
}

function read(error: unknown, fallback: string): string {
  return error instanceof ServiceError ? error.message : fallback
}
